import moment from 'moment';
import 'moment-timezone';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import UserConstants from 'constants/UserConstants';
import {ClientId, FromServerId, ToServerId} from 'notebook/ELNConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {addCid} from 'notebook/store/StoreTransformUtil';
import {TableValidationCellError} from 'notebook/structured-tables/StructuredTableApiConstants';
import {BaseCellError} from 'notebook/tables/errors/TableErrorConstants';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import Preferences from 'Preferences';

import {OutputTableMap} from './excess/EvaluationConstants';
import {CellErrorJSON} from './excess/Util';
import {
  CustomResolutionQuery,
  CustomResolutionQueryType,
  StringResolutionQuery,
} from './string-resolution/StringResolutionConstants';

// TODO(BNCH-12478): Finalize shape
export type EvaluationSnapshot = OutputTableMap;

export enum ResolutionQueryType {
  STRING = 'STRING',
  CUSTOM = 'CUSTOM',
}

export interface QueryResolution {
  // Backend should prevent persisting null value, but ensure signed out user can open readonly entry.
  userId: string | null;
  timestamp: string;
  provenanceUuid: string;
  resolvedValue: TypedValue | null;
  error: CellErrorJSON | null;
  query: StringResolutionQuery | CustomResolutionQuery;
}

export interface ValidationErrorSnapshot {
  [tableName: string]: Array<TableValidationCellError>;
}

export type BaseNoteRuntimeSnapshot<TIds> = {
  excessVersion: string | null;
  evaluationSnapshot: EvaluationSnapshot | null;
  validationErrorSnapshot: ValidationErrorSnapshot | null;
  queryResolutions: Array<QueryResolution>;
  languageTag: string;
  timezone: string | null;
} & TIds;

export interface NoteRuntimeSnapshot extends BaseNoteRuntimeSnapshot<ClientId> {
  isEvaluationPersisted: boolean;
  /** Query resolutions present at the start of the most recent evaluation round, before new resolutions were merged in. Stripped on save. */
  prevQueryResolutions?: Array<QueryResolution>;
}
export interface FromServerNoteRuntimeSnapshot extends BaseNoteRuntimeSnapshot<FromServerId> {}
export interface ToServerNoteRuntimeSnapshot extends BaseNoteRuntimeSnapshot<ToServerId> {
  bootstrapped?: never;
}

export function createNoteRuntimeSnapshot(languageTag?: string, timezone?: string): NoteRuntimeSnapshot {
  return {
    cid: _.uniqueId('note-runtime-snapshot'),
    // These will be bootstrapped on first evaluation
    excessVersion: null,
    evaluationSnapshot: null,
    isEvaluationPersisted: false,
    validationErrorSnapshot: null,
    queryResolutions: [],
    // Use provided languageTag, or fall back to browser's navigator global. In cases like in tests fallback to a sensible value.
    languageTag: languageTag ?? Preferences.get(UserConstants.LANGUAGE_PREFERENCE_KEY) ?? 'en-US',
    // Use the provided timezone, else seed with the browser's IANA zone name so ft_datetime
    // serialization is stable and DST-aware regardless of which evaluator (browser or NES) runs.
    // moment.tz.guess() yields e.g. "America/Los_Angeles".
    timezone: timezone ?? moment.tz.guess(),
  };
}

export function prepareNoteRuntimeSnapshotForStore(
  noteRuntimeSnapshot: FromServerNoteRuntimeSnapshot,
  cidIdMap: CidIdMap
  // @ts-expect-error
): asserts noteRuntimeSnapshot is NoteRuntimeSnapshot {
  addCid(noteRuntimeSnapshot, 'note-runtime-snapshot', cidIdMap);
  // @ts-expect-error - we're mutating the object to add this property
  noteRuntimeSnapshot.isEvaluationPersisted = noteRuntimeSnapshot.evaluationSnapshot != null;
}

function onlyLookupQueries(queryResolutions: Array<QueryResolution>): Array<QueryResolution> {
  return queryResolutions.filter(
    (r) =>
      r.query.type === ResolutionQueryType.CUSTOM &&
      r.query.customResolutionQueryType === CustomResolutionQueryType.LOOKUP_COLUMN
  );
}

export function filterLookupColumnQueries(
  queryResolutions: Array<QueryResolution>,
  provenanceUuid: string,
  lookupConfigIds?: Array<string>
): Array<QueryResolution> {
  return queryResolutions.filter(
    (r) =>
      !(
        r.query.type === ResolutionQueryType.CUSTOM &&
        r.query.customResolutionQueryType === CustomResolutionQueryType.LOOKUP_COLUMN &&
        r.provenanceUuid === provenanceUuid &&
        (!lookupConfigIds || lookupConfigIds.includes(r.query.lookupConfigId))
      )
  );
}

function onlyStringResolutionQueries(queryResolutions: Array<QueryResolution>): Array<QueryResolution> {
  return queryResolutions.filter((r) => r.query.type === ResolutionQueryType.STRING);
}

// `resolutionAction` (a callback) and `resolutionActionLabel` (its button text) are attached
// to cell errors at render time purely for UI affordances and shouldn't be persisted in the
// evaluation snapshot.
function stripResolutionActionsFromError(error: BaseCellError): BaseCellError {
  if (error.resolutionAction === undefined && error.resolutionActionLabel === undefined) {
    return error;
  }
  // Preserve both prototype behavior and non-enumerable fields (e.g. Error.message).
  const stripped: BaseCellError = Object.create(Object.getPrototypeOf(error));
  Object.defineProperties(stripped, Object.getOwnPropertyDescriptors(error));
  delete stripped.resolutionAction;
  delete stripped.resolutionActionLabel;
  return stripped;
}

function stripResolutionActionsFromSnapshot(snapshot: EvaluationSnapshot): EvaluationSnapshot {
  const stripped: EvaluationSnapshot = {};
  for (const tableName of Object.keys(snapshot)) {
    stripped[tableName] = snapshot[tableName].map((row) =>
      row.map((cell) => {
        if (cell.error == null) {
          return cell;
        }
        const error = Array.isArray(cell.error)
          ? cell.error.map((singleError) => stripResolutionActionsFromError(singleError))
          : stripResolutionActionsFromError(cell.error);
        return {...cell, error};
      })
    );
  }
  return stripped;
}

export function prepareNoteRuntimeSnapshotForSave(
  noteRuntimeSnapshot: NoteRuntimeSnapshot,
  cidIdMap: CidIdMap
): ToServerNoteRuntimeSnapshot | null {
  const {queryResolutions, excessVersion, cid} = noteRuntimeSnapshot;

  if (excessVersion == null) {
    return null;
  }

  const {isEvaluationPersisted, prevQueryResolutions, ...noteRuntimeSnapshotForServer} = noteRuntimeSnapshot;

  return {
    ...noteRuntimeSnapshotForServer,
    evaluationSnapshot:
      noteRuntimeSnapshotForServer.evaluationSnapshot != null
        ? stripResolutionActionsFromSnapshot(noteRuntimeSnapshotForServer.evaluationSnapshot)
        : noteRuntimeSnapshotForServer.evaluationSnapshot,
    // Backfill the timezone for snapshots that predate this field (loaded from the server with a null
    // timezone): guess the browser's IANA zone on save so ft_datetime serialization becomes stable and
    // DST-aware on the next evaluation. A snapshot that already has a zone keeps it. Gated on the flag
    // so timezone is only populated when the feature is enabled.
    timezone:
      BenchlingConfig.ELN_USE_NRS_TIMEZONE_IN_EVALUATION && noteRuntimeSnapshot.timezone == null
        ? moment.tz.guess()
        : noteRuntimeSnapshot.timezone,
    queryResolutions: onlyLookupQueries(queryResolutions).concat(
      onlyStringResolutionQueries(queryResolutions)
    ),
    id: cidIdMap[cid] as number,
  };
}
